export type UseCaseNavLink = {
  slug: string
  navLabel?: string
  seoTitle: string
  seoDescription: string
  navDescription?: string
}

export const USE_CASE_NAV_QUERY = `*[_type == "useCasePage" && !(_id in path("drafts.**")) && publishedAt <= now()]
  | order(navLabel asc, seoTitle asc) {
    "slug": slug.current,
    navLabel,
    seoTitle,
    seoDescription,
    navDescription
  }`

export type UseCaseImage = {
  asset: unknown
  alt?: string
  hotspot?: unknown
  width?: number
  height?: number
}

export type UseCaseChip = {
  label: string
}

export type UseCaseTableBlock = {
  caption?: string
  style?: string
  size?: string
  alignment?: string
  highlightColor?: string
  highlightedColumnIndex?: number
  hasHeaderRow?: boolean
  hasHeaderColumn?: boolean
  hasFooterRow?: boolean
  isFullWidth?: boolean
  rows?: {
    cells?: {
      content?: string
      isBold?: boolean
      badge?: boolean
      badgeColor?: string
      icon?: string
      link?: string
    }[]
  }[]
  footnote?: string
  sourceText?: string
  sourceUrl?: string
}

export type UseCaseStat = {
  value: string
  label: string
  detail?: string
}

export type UseCaseToneOverride = 'dark' | 'light' | ''

type SectionBase = {
  _key: string
  _type: string
  toneOverride?: UseCaseToneOverride
}

export type UcHeroSection = SectionBase & {
  _type: 'ucHero'
  eyebrow?: string
  headingMain?: string
  headingAccent?: string
  body?: string
  chips?: UseCaseChip[]
  primaryCtaLabel?: string
  primaryCtaUrl?: string
  secondaryCtaLabel?: string
  secondaryCtaUrl?: string
  footnote?: string
  image?: UseCaseImage
}

export type UcProblemStatsSection = SectionBase & {
  _type: 'ucProblemStats'
  sceneNumber?: string
  sceneLabel?: string
  heading?: string
  stats?: UseCaseStat[]
  quote?: string
}

export type UcBenefitStatsSection = SectionBase & {
  _type: 'ucBenefitStats'
  eyebrow?: string
  cells?: UseCaseStat[]
}

export type UcWorkflowStepSection = SectionBase & {
  _type: 'ucWorkflowStep'
  sceneNumber?: string
  sceneLabel?: string
  stepNumber?: string
  kicker?: string
  heading?: string
  headingAccent?: string
  body?: string
  chips?: UseCaseChip[]
  chipStyle?: 'tag' | 'model'
  image?: UseCaseImage
}

export type UcFormatGridSection = SectionBase & {
  _type: 'ucFormatGrid'
  sceneNumber?: string
  sceneLabel?: string
  heading?: string
  headingAccent?: string
  subheading?: string
  cards?: {
    tag: string
    title: string
    description: string
    image?: UseCaseImage
  }[]
}

export type UcFeaturesSection = SectionBase & {
  _type: 'ucFeatures'
  eyebrow?: string
  heading?: string
  features?: { title: string; description: string }[]
}

export type UcComparisonTableSection = SectionBase & {
  _type: 'ucComparisonTable'
  eyebrow?: string
  heading?: string
  table?: UseCaseTableBlock
}

export type UcTestimonialSection = SectionBase & {
  _type: 'ucTestimonial'
  sceneNumber?: string
  sceneLabel?: string
  quote?: string
  attribution?: string
  role?: string
}

export type UcPricingCalloutSection = SectionBase & {
  _type: 'ucPricingCallout'
  eyebrow?: string
  heading?: string
  body?: string
  ctaLabel?: string
  ctaUrl?: string
}

export type UcFaqSection = SectionBase & {
  _type: 'ucFaq'
  eyebrow?: string
  heading?: string
  headingAccent?: string
  items?: { question: string; answer: string }[]
}

export type UcCtaBannerSection = SectionBase & {
  _type: 'ucCtaBanner'
  eyebrow?: string
  headingMain?: string
  headingAccent?: string
  subtext?: string
  primaryCtaLabel?: string
  primaryCtaUrl?: string
  secondaryCtaLabel?: string
  secondaryCtaUrl?: string
  footnote?: string
}

export type UseCaseSection =
  | UcHeroSection
  | UcProblemStatsSection
  | UcBenefitStatsSection
  | UcWorkflowStepSection
  | UcFormatGridSection
  | UcFeaturesSection
  | UcComparisonTableSection
  | UcTestimonialSection
  | UcPricingCalloutSection
  | UcFaqSection
  | UcCtaBannerSection

export type UseCaseSectionType = UseCaseSection['_type']

export type UseCasePageData = {
  _id: string
  _updatedAt?: string
  title: string
  slug: string
  publishedAt: string
  seoTitle: string
  seoDescription: string
  ogImage: UseCaseImage
  navLabel?: string
  navDescription?: string
  sections?: UseCaseSection[]
  // Legacy flat fields — kept for backward compatibility until documents are migrated
  hero?: {
    eyebrow?: string
    headingMain?: string
    headingAccent?: string
    body?: string
    primaryCtaLabel?: string
    primaryCtaUrl?: string
    secondaryCtaLabel?: string
    secondaryCtaUrl?: string
    footnote?: string
    media?: UseCaseImage
  }
  problem?: {
    eyebrow?: string
    heading?: string
    body?: string
  }
  comparisonTable?: {
    eyebrow?: string
    heading?: string
    table?: UseCaseTableBlock
  }
  workflow?: {
    eyebrow?: string
    heading?: string
    subheading?: string
    steps?: {
      stepNumber: string
      title: string
      description: string
    }[]
  }
  useCasesGrid?: {
    eyebrow?: string
    heading?: string
    cards?: {
      title: string
      description: string
      image?: UseCaseImage
      badgeLabel?: string
    }[]
  }
  features?: {
    eyebrow?: string
    heading?: string
    features?: {
      title: string
      description: string
    }[]
  }
  pricingCallout?: {
    eyebrow?: string
    heading?: string
    body?: string
    ctaLabel?: string
    ctaUrl?: string
  }
  faq?: {
    eyebrow?: string
    heading?: string
    headingAccent?: string
    items?: {
      question: string
      answer: string
    }[]
  }
  ctaBanner?: {
    eyebrow?: string
    headingMain?: string
    headingAccent?: string
    subtext?: string
    primaryCtaLabel?: string
    primaryCtaUrl?: string
    secondaryCtaLabel?: string
    secondaryCtaUrl?: string
    footnote?: string
  }
}

export type UseCaseSitemapEntry = {
  path: string
  lastmod?: string
}

export const USE_CASE_IMAGE_PROJECTION = `{
  asset,
  alt,
  hotspot,
  "width": asset->metadata.dimensions.width,
  "height": asset->metadata.dimensions.height
}`

export const USE_CASE_TABLE_PROJECTION = `{
  caption,
  style,
  size,
  alignment,
  highlightColor,
  highlightedColumnIndex,
  hasHeaderRow,
  hasHeaderColumn,
  hasFooterRow,
  isFullWidth,
  rows[] {
    cells[] {
      content,
      isBold,
      badge,
      badgeColor,
      icon,
      link
    }
  },
  footnote,
  sourceText,
  sourceUrl
}`

const USE_CASE_SECTIONS_PROJECTION = `sections[] {
  _key,
  _type,
  toneOverride,
  eyebrow,
  heading,
  headingMain,
  headingAccent,
  subheading,
  subtext,
  body,
  quote,
  footnote,
  sceneNumber,
  sceneLabel,
  stepNumber,
  kicker,
  chipStyle,
  attribution,
  role,
  ctaLabel,
  ctaUrl,
  primaryCtaLabel,
  primaryCtaUrl,
  secondaryCtaLabel,
  secondaryCtaUrl,
  chips[] { label },
  stats[] { value, label, detail },
  cells[] { value, label, detail },
  features[] { title, description },
  items[] { question, answer },
  cards[] {
    tag,
    title,
    description,
    image ${USE_CASE_IMAGE_PROJECTION}
  },
  image ${USE_CASE_IMAGE_PROJECTION},
  table ${USE_CASE_TABLE_PROJECTION}
}`

export const USE_CASE_PAGE_PROJECTION = `{
  _id,
  _updatedAt,
  title,
  "slug": slug.current,
  publishedAt,
  seoTitle,
  seoDescription,
  ogImage ${USE_CASE_IMAGE_PROJECTION},
  navLabel,
  navDescription,
  ${USE_CASE_SECTIONS_PROJECTION},
  hero {
    eyebrow,
    headingMain,
    headingAccent,
    body,
    primaryCtaLabel,
    primaryCtaUrl,
    secondaryCtaLabel,
    secondaryCtaUrl,
    footnote,
    media ${USE_CASE_IMAGE_PROJECTION}
  },
  problem {
    eyebrow,
    heading,
    body
  },
  comparisonTable {
    eyebrow,
    heading,
    table ${USE_CASE_TABLE_PROJECTION}
  },
  workflow {
    eyebrow,
    heading,
    subheading,
    steps[] {
      stepNumber,
      title,
      description
    }
  },
  useCasesGrid {
    eyebrow,
    heading,
    cards[] {
      title,
      description,
      image ${USE_CASE_IMAGE_PROJECTION},
      badgeLabel
    }
  },
  features {
    eyebrow,
    heading,
    features[] {
      title,
      description
    }
  },
  pricingCallout {
    eyebrow,
    heading,
    body,
    ctaLabel,
    ctaUrl
  },
  faq {
    eyebrow,
    heading,
    headingAccent,
    items[] {
      question,
      answer
    }
  },
  ctaBanner {
    eyebrow,
    headingMain,
    headingAccent,
    subtext,
    primaryCtaLabel,
    primaryCtaUrl,
    secondaryCtaLabel,
    secondaryCtaUrl,
    footnote
  }
}`

export const USE_CASE_PAGE_QUERY = `*[_type == "useCasePage" && slug.current == $slug && !(_id in path("drafts.**")) && publishedAt <= now()][0]${USE_CASE_PAGE_PROJECTION}`

export const USE_CASE_SLUGS_QUERY = `*[_type == "useCasePage" && !(_id in path("drafts.**")) && publishedAt <= now()]{ "slug": slug.current, publishedAt, _updatedAt }`
